import { faDot } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';

import { ImageWithLoading } from '@/components/blocks/section-image/image-with-loading';
import { ContentDate } from '@/components/page-sections/content-date';
import { Badge } from '@/components/ui/badge';
import { ContentContainer } from '@/components/ui/content-container';
import { Icon } from '@/components/ui/icon';
import { Heading2, Heading3, Paragraph } from '@/components/ui/typography';
import { formatProfileNames, normalizeProfiles } from '../../profile-list';
import type { BlogWidgetProps } from '../definition';

export function CardsVariant({ title, blogs }: BlogWidgetProps) {
  if (blogs.length === 0) return null;

  return (
    <section className="bg-muted">
      <ContentContainer as="div">
        <div className="mb-8 text-center">
          <Heading2 className="mb-4">{title}</Heading2>
          <div className="flex items-center justify-center gap-2 mx-auto">
            <div className="h-[2px] w-8 bg-accent rounded-full" />
            <div className="h-[2px] w-24 bg-primary rounded-full" />
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {blogs.map((blog, index) => {
            const authors = normalizeProfiles(blog.authors);
            const authorLabel = formatProfileNames(authors);

            return (
              <article
                key={blog.id || index}
                className="group/blog-item relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-lg transition-all hover:-translate-y-2"
              >
                {blog.imageUrl && (
                  <div className="relative h-40 w-full overflow-hidden border">
                    <ImageWithLoading
                      image={{ url: blog.imageUrl }}
                      alt={blog.title || 'Blog image'}
                      className="h-full w-full object-cover"
                      inlineSvg={false}
                    />
                  </div>
                )}
                <div className="relative flex flex-1 flex-col px-5 py-4">
                  {blog.title && (
                    <Heading3 className="mb-1 text-lg font-bold group-hover/blog-item:text-primary transition-colors line-clamp-1">
                      {blog.title}
                    </Heading3>
                  )}
                  {blog.summary && (
                    <Paragraph className="mb-3 flex-1 text-xs text-muted-foreground line-clamp-3 leading-tight">
                      {blog.summary}
                    </Paragraph>
                  )}
                  {blog.topics && blog.topics.length > 0 && (
                    <div className="mb-3 flex flex-wrap items-center gap-1.5">
                      {blog.topics.map((topic) => (
                        <Badge
                          key={topic.id || topic.name}
                          size="sm"
                          colorScheme="primary"
                          variant="solid"
                        >
                          {topic.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 pt-3 border-t border-border text-xs font-medium text-muted-foreground">
                    {authorLabel && <span>{authorLabel}</span>}
                    {blog.publishedAt && authorLabel && <Icon icon={faDot} className="size-0.5" />}
                    {blog.publishedAt && (
                      <ContentDate className="text-xs" value={blog.publishedAt} />
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </ContentContainer>
    </section>
  );
}
